
//------------------------------------------------------------------------------
// <auto-generated>
//     This code was generated from a template (PushService.tst).
//
//     Manual changes to this file may cause unexpected behavior in your application.
//     Manual changes to this file will be overwritten if the code is regenerated.
// </auto-generated>
//------------------------------------------------------------------------------

import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { HubMessageModel } from 'models/HubMessage';
import { HubAction } from 'enums/HubAction';
import { MessageType } from 'enums/MessageType';
import { QuestionCategoryType } from 'enums/QuestionCategoryType';
import { PushMessageModel, ProgressMessageModel } from 'models/PushMessage';
import { environment } from 'environments/environment';
import * as signalR from '@microsoft/signalr';



export interface IPushMessage<T> {
	action: HubAction;
	data: T;
	fromCurrentUser: boolean;
}

@Injectable()
export class PushService {

	public connection: signalR.HubConnection;
	public currentUserId?: string;

	private observables = new Map<MessageType, Subject<any>>();
	private progressObservables = new Map<string, Subject<ProgressMessageModel>>();
	private callbacks = new Map<MessageType, (hubMessage: any, fromCurrentUser: boolean) => void>();
	private progressCallbacks = new Map<string, Function>();
	private retryStartConnection = 0;

	onCommunicationLogEntryChanged: Observable<IPushMessage<{ logEntryId: string, taskActionId?: string, taskflowId?: string, companyId?: string }>>;
	onCompanyChanges: Observable<IPushMessage<{ companyId: string }>>;
	onNotificationChanges: Observable<IPushMessage<{ notificationId: string, userId: string }>>;
	onProductChange: Observable<IPushMessage<{ productId: string }>>;
	onQuestionnaireQuestionChange: Observable<IPushMessage<{ questionId: string, questionnaireId: string }>>;
	onQuestionnaireChange: Observable<IPushMessage<{ questionnaireId: string }>>;
	onQuestionReplyChange: Observable<IPushMessage<{ questionId: string, companyId: string, orderId?: string, orderLineId?: string, taskflowId?: string, categoryType: QuestionCategoryType }>>;
	onOrderChanges: Observable<IPushMessage<{ orderId: string, companyId?: string, sellerId?: string }>>;
	onSaleAgreementChange: Observable<IPushMessage<{ saleAgreementId: string, companyId?: string }>>;
	onTaskflowTemplateChanges: Observable<IPushMessage<{  }>>;
	onTaskflowChanges: Observable<IPushMessage<{ taskflowId: string, taskActionId?: string, userId?: string, companyId?: string }>>;
	onPoolChanges: Observable<IPushMessage<{ poolId: string }>>;
	onCrayonChanges: Observable<IPushMessage<{ companyId: string }>>;
	onSiteChanges: Observable<IPushMessage<{ siteId: string, productPlanId?: string }>>;
	onWorkflowTemplateChanges: Observable<IPushMessage<{  }>>;
	onProductPlanChanges: Observable<IPushMessage<{ productPlanId: string, companyId: string }>>;
	onProductPlanSettingChanges: Observable<IPushMessage<{ productPlanSettingId: string }>>;
	onSettlementChanges: Observable<IPushMessage<{ companyId?: string, settlementId?: string }>>;
	onFileReferenceChanges: Observable<IPushMessage<{ fileReferenceIds: string[] }>>;
	
	constructor() {
		this.connection = new signalR.HubConnectionBuilder()
			.withUrl(environment.gormSignalRUrl + '/messageHub')
			.withAutomaticReconnect([0, 200, 500, 1000, 1000, 1000, 1000, 1000]) // Reconnect policy [0s, 200ms, 500ms, 1s, 1s, 1s, 1s, 1s]
			.build();

		this.startConnection();

		this.connection.on('NewPushMessage', (result: PushMessageModel) => {
			if (!this.observables.has(result.messageType) || !this.callbacks.has(result.messageType)) return;
			const fromCurrentUser = this.currentUserId != undefined && result.originatingUserId == this.currentUserId;
			this.callbacks.get(result.messageType)!(result.hubMessage, fromCurrentUser);
		});

		this.connection.on('NewProgressMessage', (result: ProgressMessageModel) => {
			if (!this.progressObservables.has(result.referenceId!) || !this.progressCallbacks.has(result.referenceId!)) return;
			this.progressCallbacks.get(result.referenceId!)!(result);
		});

		this.onCommunicationLogEntryChanged = this.newObservable(MessageType.CommunicationLogEntryChanged)
			.pipe(map((result: { message: HubMessageModel<{ logEntryId: string, taskActionId?: string, taskflowId?: string, companyId?: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { logEntryId: '', taskActionId: undefined, taskflowId: undefined, companyId: undefined }, action: 0, fromCurrentUser: false };
			}));
		this.onCompanyChanges = this.newObservable(MessageType.CompanyChanges)
			.pipe(map((result: { message: HubMessageModel<{ companyId: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { companyId: '' }, action: 0, fromCurrentUser: false };
			}));
		this.onNotificationChanges = this.newObservable(MessageType.NotificationChanges)
			.pipe(map((result: { message: HubMessageModel<{ notificationId: string, userId: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { notificationId: '', userId: '' }, action: 0, fromCurrentUser: false };
			}));
		this.onProductChange = this.newObservable(MessageType.ProductChange)
			.pipe(map((result: { message: HubMessageModel<{ productId: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { productId: '' }, action: 0, fromCurrentUser: false };
			}));
		this.onQuestionnaireQuestionChange = this.newObservable(MessageType.QuestionnaireQuestionChange)
			.pipe(map((result: { message: HubMessageModel<{ questionId: string, questionnaireId: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { questionId: '', questionnaireId: '' }, action: 0, fromCurrentUser: false };
			}));
		this.onQuestionnaireChange = this.newObservable(MessageType.QuestionnaireChange)
			.pipe(map((result: { message: HubMessageModel<{ questionnaireId: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { questionnaireId: '' }, action: 0, fromCurrentUser: false };
			}));
		this.onQuestionReplyChange = this.newObservable(MessageType.QuestionReplyChange)
			.pipe(map((result: { message: HubMessageModel<{ questionId: string, companyId: string, orderId?: string, orderLineId?: string, taskflowId?: string, categoryType: QuestionCategoryType }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { questionId: '', companyId: '', orderId: undefined, orderLineId: undefined, taskflowId: undefined, categoryType: 0 }, action: 0, fromCurrentUser: false };
			}));
		this.onOrderChanges = this.newObservable(MessageType.OrderChanges)
			.pipe(map((result: { message: HubMessageModel<{ orderId: string, companyId?: string, sellerId?: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { orderId: '', companyId: undefined, sellerId: undefined }, action: 0, fromCurrentUser: false };
			}));
		this.onSaleAgreementChange = this.newObservable(MessageType.SaleAgreementChange)
			.pipe(map((result: { message: HubMessageModel<{ saleAgreementId: string, companyId?: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { saleAgreementId: '', companyId: undefined }, action: 0, fromCurrentUser: false };
			}));
		this.onTaskflowTemplateChanges = this.newObservable(MessageType.TaskflowTemplateChanges)
			.pipe(map((result: { message: HubMessageModel<{  }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: {  }, action: 0, fromCurrentUser: false };
			}));
		this.onTaskflowChanges = this.newObservable(MessageType.TaskflowChanges)
			.pipe(map((result: { message: HubMessageModel<{ taskflowId: string, taskActionId?: string, userId?: string, companyId?: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { taskflowId: '', taskActionId: undefined, userId: undefined, companyId: undefined }, action: 0, fromCurrentUser: false };
			}));
		this.onPoolChanges = this.newObservable(MessageType.PoolChanges)
			.pipe(map((result: { message: HubMessageModel<{ poolId: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { poolId: '' }, action: 0, fromCurrentUser: false };
			}));
		this.onCrayonChanges = this.newObservable(MessageType.CrayonChanges)
			.pipe(map((result: { message: HubMessageModel<{ companyId: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { companyId: '' }, action: 0, fromCurrentUser: false };
			}));
		this.onSiteChanges = this.newObservable(MessageType.SiteChanges)
			.pipe(map((result: { message: HubMessageModel<{ siteId: string, productPlanId?: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { siteId: '', productPlanId: undefined }, action: 0, fromCurrentUser: false };
			}));
		this.onWorkflowTemplateChanges = this.newObservable(MessageType.WorkflowTemplateChanges)
			.pipe(map((result: { message: HubMessageModel<{  }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: {  }, action: 0, fromCurrentUser: false };
			}));
		this.onProductPlanChanges = this.newObservable(MessageType.ProductPlanChanges)
			.pipe(map((result: { message: HubMessageModel<{ productPlanId: string, companyId: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { productPlanId: '', companyId: '' }, action: 0, fromCurrentUser: false };
			}));
		this.onProductPlanSettingChanges = this.newObservable(MessageType.ProductPlanSettingChanges)
			.pipe(map((result: { message: HubMessageModel<{ productPlanSettingId: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { productPlanSettingId: '' }, action: 0, fromCurrentUser: false };
			}));
		this.onSettlementChanges = this.newObservable(MessageType.SettlementChanges)
			.pipe(map((result: { message: HubMessageModel<{ companyId?: string, settlementId?: string }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { companyId: undefined, settlementId: undefined }, action: 0, fromCurrentUser: false };
			}));
		this.onFileReferenceChanges = this.newObservable(MessageType.FileReferenceChanges)
			.pipe(map((result: { message: HubMessageModel<{ fileReferenceIds: string[] }>, fromCurrentUser: boolean }) => {
				if (result.message.data) return { data: result.message.data, action: result.message.action, fromCurrentUser: result.fromCurrentUser };
				else return { data: { fileReferenceIds: [] }, action: 0, fromCurrentUser: false };
			}));
	}

	public onChanges<T>(messageType: MessageType): Observable<IPushMessage<T>> {
		return this.newObservable(messageType)
			.pipe(map((result: { message: HubMessageModel<T>, fromCurrentUser: boolean }) => {
				return { action: result.message.action, data: result.message.data!, fromCurrentUser: result.fromCurrentUser };
			}));
	}

	public onProgressChanges(referenceId: string): Observable<ProgressMessageModel> {
		return this.newProgressObservable(referenceId)
			.pipe(map((result: ProgressMessageModel) => result));
	}

	get connectionState() {
		return this.retryStartConnection > 0 ? signalR.HubConnectionState.Reconnecting : this.connection.state;
	}

	public startConnection(resetCounter = true) {
		if (this.connection.state != signalR.HubConnectionState.Disconnected)
			return;
		if (resetCounter) this.retryStartConnection = 0;
		this.connection.start()
			.then(() => {
				this.retryStartConnection = 0;
				console.log('conn started');
			})
			.catch(err => {
				this.retryStartConnection++;
				console.log('error signalr conn: ' + err);
				if (this.retryStartConnection == 3) {
					this.retryStartConnection = 0;
					console.log('signalr conn failed 3 times, stopping retry');
					return;
				}
				setTimeout(() => {
					this.startConnection(false);
				}, 3000);
			});
	}

	public stopConnection() {
		this.connection.stop();
	}

	private newObservable(messageType: MessageType): Subject<{ message: any, fromCurrentUser: boolean }> {
		if (!this.observables.has(messageType)) {
			let subject = new Subject();
			this.observables.set(messageType, subject);
			this.callbacks.set(messageType, (message: any, fromCurrentUser: boolean) => {
				subject.next({ message, fromCurrentUser });
			});
		}
		return this.observables.get(messageType)!;
	}

	private newProgressObservable(referenceId: string): Subject<ProgressMessageModel> {
		if (!this.progressObservables.has(referenceId)) {
			let subject = new Subject<ProgressMessageModel>();
			this.progressObservables.set(referenceId, subject);
			this.progressCallbacks.set(referenceId, (data: ProgressMessageModel) => {
				subject.next(data);
			});
		}
		return this.progressObservables.get(referenceId)!;
	}
}

